import { Calendar, Instagram, MessageSquareText, Scissors } from "lucide-react";
import { BARBERS } from "../data/site";
import { Reveal } from "./Reveal";

const barberHref = (barber) => {
  if (barber.sms) {
    return `sms:${barber.smsNumber}?&body=${encodeURIComponent(barber.smsBody)}`;
  }
  return barber.bookingUrl || "#";
};

const BarberCard = ({ barber, index }) => (
  <Reveal
    delay={index * 0.08}
    className="w-full sm:w-[calc(50%-0.75rem)] lg:w-[calc((100%-3rem)/3)]"
  >
    <article data-testid={`barber-card-${barber.id}`} className="group h-full">
      <div className="relative aspect-[4/5] overflow-hidden rounded-xl bg-[#111a2c] ring-1 ring-black/[0.06]">
        {barber.photo ? (
          <img
            data-testid={`barber-photo-${barber.id}`}
            src={barber.photo}
            alt={`${barber.name} — barber at Phade Barbers in Calhoun, Georgia`}
            loading="lazy"
            className="absolute inset-0 h-full w-full object-cover object-top"
          />
        ) : (
          <>
            {/* Neutral placeholder — swap for the barber's real portrait */}
            <div className="absolute inset-0 bg-gradient-to-br from-[#1a2438] via-[#111a2c] to-[#0a101d]" />
            <Scissors
              className="absolute left-1/2 top-[38%] h-28 w-28 -translate-x-1/2 -translate-y-1/2 text-white/[0.05]"
              strokeWidth={1}
            />
            <span className="text-stroke-ice absolute left-1/2 top-[38%] -translate-x-1/2 -translate-y-1/2 text-6xl font-black tracking-tight">
              {barber.initials}
            </span>
            <span
              data-testid={`barber-photo-placeholder-${barber.id}`}
              className="absolute left-1/2 top-[58%] -translate-x-1/2 whitespace-nowrap rounded-full border border-white/15 px-3 py-1 text-[10px] font-semibold tracking-[0.18em] text-white/45"
            >
              PORTRAIT COMING SOON
            </span>
          </>
        )}

        <a
          data-testid={`barber-instagram-${barber.id}`}
          href={barber.instagramUrl || "https://instagram.com/"}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`${barber.name} on Instagram`}
          className="absolute right-3.5 top-3.5 rounded-full bg-black/45 p-2.5 text-white/85 backdrop-blur-sm transition-colors duration-300 hover:bg-[#4a9bdc] hover:text-white"
        >
          <Instagram className="h-4 w-4" />
        </a>

        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#080d17] via-[#080d17]/75 to-transparent px-5 pb-5 pt-16">
          <h3 className="text-xl font-extrabold tracking-tight text-white">{barber.name}</h3>
          <div className="mt-2.5 flex flex-wrap items-center gap-x-4 gap-y-1.5">
            <span className="text-[11px] font-bold tracking-[0.14em] text-[#9fd3f0]">
              {barber.years} YEARS EXPERIENCE
            </span>
            <a
              data-testid={`barber-view-work-${barber.id}`}
              href={barber.instagramUrl || "https://instagram.com/"}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[11px] font-bold tracking-[0.14em] text-white/55 underline-offset-4 transition-colors hover:text-white hover:underline"
            >
              VIEW MY WORK
            </a>
          </div>
        </div>
      </div>

      <a
        data-testid={`barber-book-${barber.id}`}
        href={barberHref(barber)}
        target={barber.bookingUrl ? "_blank" : undefined}
        rel={barber.bookingUrl ? "noopener noreferrer" : undefined}
        className="mt-3 flex items-center justify-center gap-2.5 rounded-lg bg-[#4a9bdc] px-6 py-4 text-[13px] font-bold tracking-[0.1em] text-white transition-all duration-300 hover:bg-[#5aaae8] group-hover:shadow-[0_10px_36px_-12px_rgba(74,155,220,0.65)]"
      >
        {barber.sms ? (
          <MessageSquareText className="h-4 w-4" />
        ) : (
          <Calendar className="h-4 w-4" />
        )}
        {barber.cta}
      </a>
    </article>
  </Reveal>
);

export const Barbers = () => (
  <section id="barbers" data-testid="barbers-section" className="bg-[#f6f7f9] py-20 sm:py-28">
    <div className="mx-auto max-w-[1240px] px-5 sm:px-8">
      <Reveal>
        <h2 className="text-4xl font-black uppercase tracking-[-0.02em] text-[#161e2b] sm:text-5xl lg:text-6xl">
          MEET THE BARBERS
        </h2>
        <p className="mt-5 max-w-2xl text-base leading-relaxed text-[#5c6675] md:text-lg">
          Five barbers. One shop. Find the barber whose style fits you and book
          directly with them.
        </p>
      </Reveal>

      <div className="mt-14 flex flex-wrap justify-center gap-6">
        {BARBERS.map((barber, i) => (
          <BarberCard key={barber.id} barber={barber} index={i} />
        ))}
      </div>
    </div>
  </section>
);
